// InstructionNode shows a robot's instruction.

import { InstructionType } from 'app/src/engine/Instruction';
import * as React from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

export const InstructionNodeDragType = 'InstructionNode';

export type InstructionNodeDragData = {
	type: 'InstructionNode';
	instructionType: InstructionType;
};

export type InstructionNodeDropResult = {
	handled: boolean;
};

type Props = {
	type: InstructionType;
	disabled?: boolean;
	highlighted?: boolean;
	onDrag?: () => void;
	onDrop?: (handled: boolean) => void;
};

function getInnerClassName(type: InstructionType): string {
	switch (type) {
		case InstructionType.Move1:
			return 'InstructionNode-inner--Move1';
		case InstructionType.Move2:
			return 'InstructionNode-inner--Move2';
		case InstructionType.Move3:
			return 'InstructionNode-inner--Move3';
		case InstructionType.BackUp:
			return 'InstructionNode-inner--BackUp';
		case InstructionType.TurnLeft:
			return 'InstructionNode-inner--TurnLeft';
		case InstructionType.TurnRight:
			return 'InstructionNode-inner--TurnRight';
		case InstructionType.UTurn:
			return 'InstructionNode-inner--UTurn';
		case InstructionType.Halt:
			return 'InstructionNode-inner--Halt';
		default:
			throw new Error('Invalid instruction type.');
	}
}

export default function InstructionNode({
	type,
	disabled,
	highlighted,
	onDrag,
	onDrop,
}: Props): React.ReactElement | null {
	const containerRef = React.useRef<HTMLDivElement | null>(null);

	const [, drag, preview] = useDrag({
		item: { type: InstructionNodeDragType, instructionType: type },
		begin: () => {
			if (onDrag) {
				onDrag();
			}
		},
		end: (_, monitor) => {
			let dropHandled = false;

			if (monitor.didDrop()) {
				const { handled } = monitor.getDropResult();
				dropHandled = !!handled;
			}

			if (onDrop) {
				onDrop(dropHandled);
			}
		},
		canDrag: () => !disabled,
	});

	drag(containerRef);

	React.useEffect(() => {
		preview(getEmptyImage());
	}, [preview]);

	const instructionNodeClassNames = ['InstructionNode'];

	if (disabled) {
		instructionNodeClassNames.push('InstructionNode--disabled');
	}

	if (highlighted) {
		instructionNodeClassNames.push('InstructionNode--highlighted');
	}

	const instructionNodeInnerClassNames = ['InstructionNode-inner', getInnerClassName(type)];

	return (
		<div className={instructionNodeClassNames.join(' ')} ref={containerRef}>
			<div className={instructionNodeInnerClassNames.join(' ')}></div>
		</div>
	);
}
